import React, { Suspense, lazy, useEffect } from "react";
import { useNavigate } from "react-router-dom";

/* Loads a page/tab only when it is first shown, instead of bundling every
   page into the first download. The returned component carries its own
   small fallback so the rest of the screen never blanks while it loads. */
export const PageLoading = ({ label = "Loading..." }) => (
  <div
    style={{
      padding: 24,
      textAlign: "center",
      color: "#475569",
      fontWeight: 600,
    }}
  >
    {label}
  </div>
);

// After a new deploy, a phone still running the old version asks for page
// files that no longer exist ("Loading chunk failed"). Reload once to pick up
// the new version instead of showing a broken page.
const RELOAD_FLAG = "servicing_chunk_reload_at";
export const RESUME_KEY = "servicing_resume_path";

/** Inside the Router: after a version reload, go back to the page we were on. */
export const ResumeAfterReload = () => {
  const navigate = useNavigate();
  useEffect(() => {
    let path = "";
    try {
      path = sessionStorage.getItem(RESUME_KEY) || "";
      sessionStorage.removeItem(RESUME_KEY);
    } catch (e) {
      // ignore
    }
    if (path && path !== "/" && path.startsWith("/")) navigate(path, { replace: true });
  }, [navigate]);
  return null;
};

const withChunkRetry = (loader) => () =>
  loader()
    .then((mod) => {
      try {
        sessionStorage.removeItem(RELOAD_FLAG);
      } catch (e) {
        // ignore
      }
      return mod;
    })
    .catch((error) => {
      const isChunkError =
        /Loading chunk|ChunkLoadError|Failed to fetch dynamically imported module|Importing a module script failed/i.test(
          String(error?.message || error?.name || error)
        );
      let lastReload = 0;
      try {
        lastReload = Number(sessionStorage.getItem(RELOAD_FLAG) || 0);
      } catch (e) {
        // ignore
      }
      if (isChunkError && Date.now() - lastReload > 30000) {
        try {
          sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
        } catch (e) {
          // ignore
        }
        // Reload from the app's front door, not the page we're on. Reloading
        // a deep link like /JobChecklist/abc asked the server for
        // /JobChecklist/static/js/main.js, got the HTML page back, and showed
        // a blank screen ("Unexpected token '<'"). App.js takes us back to
        // this page once the new version has loaded (ResumeAfterReload).
        try {
          sessionStorage.setItem(
            RESUME_KEY,
            `${window.location.pathname}${window.location.search}${window.location.hash}`
          );
        } catch (e) {
          // ignore
        }
        window.location.replace("/");
        return new Promise(() => {}); // page is reloading
      }
      throw error;
    });

export const lazyWithFallback = (loader, label) => {
  const LazyComponent = lazy(withChunkRetry(loader));
  const Wrapped = (props) => (
    <Suspense fallback={<PageLoading label={label} />}>
      <LazyComponent {...props} />
    </Suspense>
  );
  return Wrapped;
};

export default lazyWithFallback;
